import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Cloud,
  RefreshCw,
  WifiOff,
  AlertTriangle,
  CheckCircle2,
  Clock,
  History,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  fetchSyncStatus,
  fetchSyncRuns,
  fetchSyncConflicts,
  triggerSyncNow,
  testSyncConnection,
  resolveSyncConflict,
  clearSyncError,
} from "../../store/slices/syncSlice";
import { fetchPatients } from "../../store/slices/patientSlice";
import { fetchTestOrders } from "../../store/slices/testOrderSlice";

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

/** Short sync id for tables (UUIDs are hard to scan) */
const shortSyncId = (id) => {
  if (id == null || id === "") return "—";
  const s = String(id);
  if (s.length <= 14) return s;
  return `${s.slice(0, 8)}…${s.slice(-4)}`;
};

const ENTITY_LABELS = {
  users: "User",
  clinics: "Clinic",
  sample_types: "Sample type",
  tube_types: "Tube type",
  test_categories: "Test category",
  sample_type_tube_types: "Sample type ↔ tube link",
  sample_type_parameters: "Sample type parameter",
  sample_type_parameter_reference_ranges: "Template reference range",
  tests: "Test",
  test_sample_types: "Test ↔ sample type",
  test_parameters: "Test parameter",
  parameter_reference_ranges: "Parameter reference range",
  dynamic_questions: "Dynamic question",
  patients: "Patient",
  visits: "Visit",
  test_orders: "Test order",
  test_question_answers: "Question answer",
  test_results: "Test result",
  parameter_results: "Parameter result",
  activity_logs: "Activity log",
  pathologist_reports: "Pathologist report",
};

const labelEntity = (type) => {
  if (!type) return "Record";
  return ENTITY_LABELS[type] || type.replace(/_/g, " ");
};

const formatDirection = (dir) => {
  const d = String(dir || "both").toLowerCase();
  if (d === "push") return "Push";
  if (d === "pull") return "Pull";
  return "Both";
};

const runStatusPresentation = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "completed") {
    return { label: "Completed", className: "bg-emerald-50 text-emerald-800 border-emerald-100" };
  }
  if (s === "noop") {
    return { label: "No changes", className: "bg-slate-50 text-slate-700 border-slate-200" };
  }
  if (s === "failed") {
    return { label: "Failed", className: "bg-red-50 text-red-800 border-red-100" };
  }
  if (s === "skipped") {
    return { label: "Skipped", className: "bg-slate-50 text-slate-600 border-slate-200" };
  }
  if (s === "queued") {
    return { label: "Queued", className: "bg-amber-50 text-amber-800 border-amber-100" };
  }
  return { label: status || "Unknown", className: "bg-amber-50 text-amber-900 border-amber-100" };
};

const SyncCenter = () => {
  const dispatch = useDispatch();
  const {
    status,
    runs,
    conflicts,
    loadingStatus,
    loadingRuns,
    loadingConflicts,
    triggeringSync,
    testingConnection,
    resolvingConflictId,
    lastManualSync,
    error,
  } = useSelector((state) => state.sync);

  const [activeTab, setActiveTab] = useState("overview");

  const refreshAll = useCallback(() => {
    dispatch(clearSyncError());
    dispatch(fetchSyncStatus());
    dispatch(fetchSyncRuns());
    dispatch(fetchSyncConflicts());
  }, [dispatch]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const pendingCount = status?.pendingLocalChanges ?? 0;

  const currentState = useMemo(() => {
    if (loadingStatus && !status) {
      return {
        label: "Checking…",
        tone: "neutral",
        description: "Reading sync status from this workstation",
      };
    }

    if (error || status?.lastError) {
      return {
        label: "Error",
        tone: "error",
        description: typeof status?.lastError === "string" ? status.lastError : error,
      };
    }

    const online = status?.online ?? status?.connection?.online;
    const lastPush = status?.lastPushAt;
    const lastPull = status?.lastPullAt;

    if (online === false) {
      return {
        label: pendingCount > 0 ? `Offline · ${pendingCount} pending` : "Offline",
        tone: "warning",
        description:
          pendingCount > 0
            ? "Changes are saved locally and will upload when the host is reachable."
            : "Cannot reach the host. Check network or Sync settings.",
      };
    }

    if (pendingCount > 0) {
      return {
        label: `Online · ${pendingCount} pending`,
        tone: "info",
        description: "There are local changes waiting for the next successful sync.",
      };
    }

    if (lastPush || lastPull) {
      return {
        label: "Online · Up to date",
        tone: "success",
        description: `Last activity: ${formatDateTime(lastPull || lastPush)}`,
      };
    }

    return {
      label: "Online",
      tone: "info",
      description: "Connected. Run a sync when you have local changes.",
    };
  }, [status, loadingStatus, error, pendingCount]);

  const toneClasses = {
    success: {
      badge: "bg-emerald-50 text-emerald-700 border-emerald-100",
      dot: "bg-emerald-500",
      icon: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
    },
    warning: {
      badge: "bg-amber-50 text-amber-800 border-amber-100",
      dot: "bg-amber-500",
      icon: <WifiOff className="w-4 h-4 text-amber-600" />,
    },
    error: {
      badge: "bg-red-50 text-red-700 border-red-100",
      dot: "bg-red-500",
      icon: <AlertTriangle className="w-4 h-4 text-red-500" />,
    },
    info: {
      badge: "bg-sky-50 text-sky-800 border-sky-100",
      dot: "bg-sky-500",
      icon: <Cloud className="w-4 h-4 text-sky-600" />,
    },
    neutral: {
      badge: "bg-gray-50 text-gray-700 border-gray-100",
      dot: "bg-gray-400",
      icon: <Cloud className="w-4 h-4 text-gray-500" />,
    },
  };

  const tone = toneClasses[currentState.tone || "info"];

  const handleSyncNow = () => {
    dispatch(clearSyncError());
    dispatch(triggerSyncNow()).then((action) => {
      if (!action.error) {
        dispatch(fetchSyncStatus());
        dispatch(fetchSyncRuns());
        dispatch(fetchPatients());
        dispatch(fetchTestOrders({}));
        try {
          window.dispatchEvent(new CustomEvent("lis:sync-completed"));
        } catch {
          /* ignore */
        }
      }
    });
  };

  const handleTestConnection = () => {
    dispatch(clearSyncError());
    dispatch(testSyncConnection()).then((action) => {
      if (!action.error) {
        dispatch(fetchSyncStatus());
      }
    });
  };

  const handleResolveConflict = (conflict, resolution) => {
    if (conflict?.id == null) return;
    dispatch(clearSyncError());
    dispatch(resolveSyncConflict({ id: conflict.id, resolution })).then((action) => {
      if (!action.error) {
        dispatch(fetchSyncConflicts());
        dispatch(fetchSyncStatus());
      }
    });
  };

  const conflictCount = conflicts?.length ?? 0;

  const lastRunSummary = useMemo(() => {
    if (!lastManualSync) return null;
    const st = lastManualSync.status;
    const pushed = lastManualSync.itemsPushed ?? 0;
    const pulled = lastManualSync.itemsPulled ?? 0;
    if (st === "failed" || lastManualSync.online === false) {
      return { tone: "error", text: "Last sync did not complete (host offline or error)." };
    }
    if (st === "noop") {
      return { tone: "neutral", text: "Last sync: nothing new to upload or download." };
    }
    if (st === "skipped") {
      return { tone: "neutral", text: "Last sync was skipped on this server role." };
    }
    return {
      tone: "success",
      text: `Last sync: sent ${pushed}, received ${pulled}.`,
    };
  }, [lastManualSync]);

  const [expandedRunId, setExpandedRunId] = useState(null);

  const toggleRunDetails = (runId) => {
    setExpandedRunId(expandedRunId === runId ? null : runId);
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {error ? (
        <div
          role="alert"
          className="flex items-start justify-between gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          <div className="flex gap-2 min-w-0">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <span className="break-words">{error}</span>
          </div>
          <button
            type="button"
            onClick={() => dispatch(clearSyncError())}
            className="shrink-0 rounded-lg p-1 text-red-700 hover:bg-red-100"
            aria-label="Dismiss error"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      ) : null}

      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#36F1A2] to-[#00d084] flex items-center justify-center text-[#1a4a5a] shadow-md shrink-0">
            <Cloud className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-[#235F72]">Sync Center</h2>
            <p className="mt-1 text-sm text-gray-600">
              See connection status, recent sync activity, and fix conflicts between this workstation
              and the host database.
            </p>

            <div
              className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium shadow-sm ${tone.badge}`}
            >
              <span className={`w-2 h-2 rounded-full ${tone.dot}`} />
              {tone.icon}
              <span>{currentState.label}</span>
            </div>

            {currentState.description ? (
              <p className="mt-2 text-xs text-gray-500 max-w-xl">{currentState.description}</p>
            ) : null}

            {lastRunSummary ? (
              <p
                className={`mt-2 text-xs font-medium max-w-xl ${
                  lastRunSummary.tone === "error"
                    ? "text-red-600"
                    : lastRunSummary.tone === "success"
                    ? "text-emerald-700"
                    : "text-gray-600"
                }`}
              >
                {lastRunSummary.text}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <button
            type="button"
            onClick={refreshAll}
            disabled={triggeringSync}
            className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold border border-gray-200 text-[#235F72] bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loadingRuns || loadingStatus ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleTestConnection}
            disabled={testingConnection}
            className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold border border-gray-200 text-[#235F72] bg-white hover:bg-gray-50 transition-colors ${
              testingConnection ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            {testingConnection ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <WifiOff className="w-4 h-4 mr-2" />
            )}
            {testingConnection ? "Testing…" : "Test host"}
          </button>
          <button
            type="button"
            onClick={handleSyncNow}
            disabled={triggeringSync}
            className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white bg-[#235F72] hover:bg-[#1a4a5a] shadow-sm transition-colors ${
              triggeringSync ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {triggeringSync ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {triggeringSync ? "Syncing…" : "Sync now"}
          </button>
        </div>
      </div>

      {/* Tabs — text-first; conflict count only where it matters */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100">
        <div className="border-b border-gray-100 px-4 pt-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActiveTab("overview")}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === "overview"
                  ? "bg-[#235F72] text-white shadow"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <History className="w-4 h-4" />
              Status &amp; history
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("conflicts")}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === "conflicts"
                  ? "bg-[#235F72] text-white shadow"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <AlertTriangle className="w-4 h-4" />
              Conflicts
              {conflictCount > 0 ? (
                <span
                  className={`min-w-[1.5rem] px-1.5 py-0.5 rounded-md text-xs font-bold ${
                    activeTab === "conflicts" ? "bg-white/20 text-white" : "bg-red-100 text-red-700"
                  }`}
                >
                  {conflictCount}
                </span>
              ) : (
                <span className="text-xs opacity-70 font-normal">(0)</span>
              )}
            </button>
          </div>
        </div>

        {activeTab === "overview" ? (
          <div className="p-4 md:p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-emerald-100/80 p-4">
                <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                  Connection
                </div>
                <div className="mt-2 flex items-center gap-2 text-sm font-semibold text-emerald-900">
                  {tone.icon}
                  <span className="truncate">{currentState.label}</span>
                </div>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Pending locally
                </div>
                <div className="mt-2 text-3xl font-bold text-[#235F72] tabular-nums">{pendingCount}</div>
                <p className="mt-1 text-xs text-gray-500">Rows waiting to upload (estimate)</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Last upload
                </div>
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-800">
                  <Clock className="w-4 h-4 text-gray-400 shrink-0" />
                  <span>{formatDateTime(status?.lastPushAt)}</span>
                </div>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Last download
                </div>
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-800">
                  <Clock className="w-4 h-4 text-gray-400 shrink-0" />
                  <span>{formatDateTime(status?.lastPullAt)}</span>
                </div>
              </div>
            </div>

            <div className="mt-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <History className="w-4 h-4 text-[#235F72]" />
                  Recent runs
                </h3>
                {loadingRuns ? (
                  <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading
                  </span>
                ) : null}
              </div>

              <div className="overflow-x-auto border border-gray-100 rounded-xl">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Time</th>
                      <th className="px-3 py-2 text-left font-semibold">Direction</th>
                      <th className="px-3 py-2 text-left font-semibold">Out / In</th>
                      <th className="px-3 py-2 text-left font-semibold">Result</th>
                      <th className="px-3 py-2 text-left font-semibold">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {(runs && runs.length ? runs : []).map((run) => {
                      const st = runStatusPresentation(run.status);
                      const isExpanded = expandedRunId === run.id;
                      const details = run.details || [];
                      return (
                        <React.Fragment key={run.id ?? run.startedAt}>
                          <tr className="text-sm cursor-pointer" onClick={() => toggleRunDetails(run.id)}>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-800">
                              {formatDateTime(run.startedAt)}
                            </td>
                            <td className="px-3 py-2 text-gray-700">{formatDirection(run.direction)}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-800 tabular-nums">
                              <span title="Sent to host">{run.itemsPushed}</span>
                              <span className="text-gray-400 mx-1">/</span>
                              <span title="Received from host">{run.itemsPulled}</span>
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-semibold ${st.className}`}
                              >
                                {st.label}
                              </span>
                            </td>
                            <td className="px-3 py-2 max-w-md text-gray-600">
                              <div className="flex items-center gap-2">
                                <span className="line-clamp-2 flex-1" title={run.message || ""}>
                                  {run.message || "—"}
                                </span>
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 shrink-0 text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 shrink-0 text-gray-400" />
                                )}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && details.length > 0 ? (
                            <tr className="bg-gray-50">
                              <td colSpan={5} className="px-3 py-3">
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                                  {details.map((d) => (
                                    <div
                                      key={d.table}
                                      className="rounded-lg border border-gray-200 bg-white px-3 py-2"
                                    >
                                      <div className="text-xs font-medium text-gray-500 truncate">
                                        {labelEntity(d.table)}
                                      </div>
                                      <div className="mt-1 flex items-baseline gap-2">
                                        <span className="text-sm font-semibold text-emerald-700">
                                          +{Number(d.applied || 0)}
                                        </span>
                                        {Number(d.conflicted || 0) > 0 ? (
                                          <span className="text-xs font-medium text-amber-700">
                                            {d.conflicted} conflict
                                          </span>
                                        ) : null}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                    {!runs || runs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-8 text-center text-sm text-gray-500">
                          No runs yet. Use "Sync now" after you change data.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 md:p-6 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                Conflicts
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                When the same record changed on both sides, pick which copy to keep on this workstation.
                Use <span className="font-medium text-gray-800">Host</span> for the server copy, or{" "}
                <span className="font-medium text-gray-800">Local</span> for this PC.
              </p>
            </div>
            {loadingConflicts ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading conflicts…
              </div>
            ) : null}

            <div className="overflow-x-auto border border-gray-100 rounded-xl bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Type</th>
                    <th className="px-3 py-2 text-left font-semibold">Sync ID</th>
                    <th className="px-3 py-2 text-left font-semibold">Detected</th>
                    <th className="px-3 py-2 text-left font-semibold">Details</th>
                    <th className="px-3 py-2 text-left font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(conflicts && conflicts.length ? conflicts : []).map((conflict) => {
                    const entity = labelEntity(conflict.entityType);
                    const recordId = conflict.externalId;
                    const busy = resolvingConflictId === conflict.id;

                    return (
                      <tr key={conflict.id} className="bg-white">
                        <td className="px-3 py-3 align-top font-medium text-gray-900">{entity}</td>
                        <td
                          className="px-3 py-3 align-top font-mono text-xs text-gray-700"
                          title={recordId || ""}
                        >
                          {shortSyncId(recordId)}
                        </td>
                        <td className="px-3 py-3 align-top text-gray-600 whitespace-nowrap">
                          {formatDateTime(conflict.createdAt)}
                        </td>
                        <td className="px-3 py-3 align-top text-gray-600 max-w-md">
                          {conflict.summary || "Versions differ."}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="flex flex-col sm:flex-row gap-2">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => handleResolveConflict(conflict, "host")}
                              className="inline-flex items-center justify-center rounded-lg bg-emerald-600 text-white text-xs font-semibold px-3 py-2 hover:bg-emerald-700 disabled:opacity-50"
                            >
                              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                              Use host
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => handleResolveConflict(conflict, "local")}
                              className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-800 text-xs font-semibold px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Use local
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!loadingConflicts && (!conflicts || conflicts.length === 0) ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-sm text-gray-500">
                        No conflicts. If sync stops on specific tables, check the latest run note above
                        or server logs.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncCenter;
